import React, { useState, type ChangeEvent } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { ReactComponent as RadioCheckedIcon } from '@/assets/icons/radio-checked-icon.svg'
import { ReactComponent as RadioUncheckedIcon } from '@/assets/icons/radio-unckecked-icon.svg'

interface TermSuggestionsDialogProps {
  open: boolean
  onClose: () => void
  onUpdate: (target_text: string) => void
  explanation: string
  options: string[]
  loading: boolean
}

function TermSuggestionsDialog({ open, onClose, onUpdate, explanation, options, loading }: TermSuggestionsDialogProps) {
  const [replacementOption, setReplacementOption] = useState(options[0] || '')
  const [manualTerm, setManualTerm] = useState('')

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setReplacementOption(event.target.value)
  }

  const handleManualTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    setManualTerm(event.target.value)
  }

  const handleReplace = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const textToReplace = replacementOption === 'edit' ? manualTerm : replacementOption

    onUpdate(textToReplace)
    onClose()
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px',
          background: '#FFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
        },
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <DialogTitle
        sx={{
          p: '24px',
          m: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #EAECF0',
        }}
      >
        <Typography color="#000" fontSize={18} fontWeight={600} lineHeight="28px">
          Why this phrase isn’t working?
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          padding: '8px 16px !important',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <>
          {loading ? (
            <Stack alignItems="center" p={2} width="100%">
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <Typography fontSize={12} lineHeight="18px">
                {explanation}
              </Typography>
              <Stack alignItems="flex-start" direction="column" gap="6px" width="100%">
                <Typography fontSize={14} fontWeight={600} lineHeight="20px">
                  Replace with suggestion
                </Typography>
                <Typography color="#000" fontSize={12} fontWeight={500} lineHeight="20px">
                  Try one of these instead:
                </Typography>
                <RadioGroup name="replacementOption" value={replacementOption} onChange={handleOptionChange}>
                  {options.map(suggestion => (
                    <FormControlLabel
                      key={suggestion}
                      control={
                        <Radio
                          checkedIcon={<RadioCheckedIcon />}
                          icon={<RadioUncheckedIcon />}
                          size="small"
                          onClick={handleOptionChange}
                        />
                      }
                      label={suggestion}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: '#344054',
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: '20px',
                        },
                      }}
                      value={suggestion}
                    />
                  ))}
                  <FormControlLabel
                    control={
                      <Radio
                        checkedIcon={<RadioCheckedIcon />}
                        icon={<RadioUncheckedIcon />}
                        size="small"
                        onClick={handleOptionChange}
                      />
                    }
                    label="Edit manually"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: '#344054',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: '20px',
                      },
                    }}
                    value="edit"
                  />
                </RadioGroup>

                {replacementOption === 'edit' && (
                  <TextField
                    fullWidth
                    placeholder="Type your replacement term"
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': { height: 44, borderRadius: 16 },
                      '& .MuiOutlinedInput-notchedOutline': { borderRadius: '16px !important' },
                    }}
                    value={manualTerm}
                    variant="outlined"
                    onChange={handleManualTermChange}
                  />
                )}
              </Stack>
            </>
          )}
        </>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', padding: '20px 12px 12px 12px' }}>
        <Button
          onClick={() =>
            window.open(
              'https://www.speakable.io/blog-posts/understanding-phrase-verification-statuses-in-speakable',
              '_blank',
            )
          }
        >
          Learn More
        </Button>
        <Stack direction="row" spacing={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            disabled={loading || (replacementOption === 'edit' && !manualTerm?.length) || !replacementOption}
            variant="contained"
            onClick={handleReplace}
          >
            Replace
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default TermSuggestionsDialog
